/**
 * TableColumnEditor — editor de UNA columna de structured_table v2
 * (Doc 24 §3 Fase 10).
 *
 * Shape de entrada (`TableColumn`):
 *   ```
 *   { key, type, required?, validation?, enum_values?,
 *     i18n: { es: {label, placeholder?}, en: {label, placeholder?} } }
 *   ```
 *
 * Modo colapsado por default — el `TableConfigBlock` parent maneja el
 * estado `expanded` y pasa el callback `onToggle`. Vista colapsada
 * muestra: key (mono) + label del locale activo + tipo + chip required.
 * Vista expandida: sub-tabs es/en para label+placeholder + select de
 * tipo + toggle required + sub-bloque validation (reusa
 * `ValidationRulesBlock`) + `EnumValuesEditor` cuando type=enum.
 *
 * Cambios de tipo a/desde enum:
 *   - enum → otro tipo: si hay enum_values, se pide confirmación al
 *     padre (props `onTypeChange` recibe `{from, to, hasEnumValues}` y
 *     decide). Para no bloquear el flujo, el componente NO muestra el
 *     warning inline — confía en el padre.
 *   - otro → enum: se inicializa `enum_values: []` y se muestra el
 *     warning "enum requires values" desde `EnumValuesEditor`.
 *
 * Validación de `key`:
 *   - snake_case (`/^[a-z][a-z0-9_]*$/`) — warning visual si no cumple.
 *   - duplicado con otras columnas (prop `keyDuplicate`).
 */

import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type {
  BlockType,
  EnumValue,
  TableColumn,
  ValidationRule,
} from '../../../api/templates'

import { EnumValuesEditor } from './EnumValuesEditor'
import { ValidationRulesBlock } from './ValidationRulesBlock'
import type { SupportedLocale } from '../../../i18n/config'

const KEY_PATTERN = /^[a-z][a-z0-9_]*$/

const COLUMN_TYPES: ReadonlyArray<TableColumn['type']> = [
  'plain_text',
  'enum',
  'number',
  'date',
  'currency',
  'autonum',
]

export interface TableColumnEditorProps {
  column: TableColumn
  /** Si la `key` de esta columna está duplicada con otra en el mismo
   * `table_config`. Calculado por el padre. */
  keyDuplicate?: boolean
  /** Locale activo del designer — solo se usa para etiquetar la vista
   * colapsada (la edición ocurre en sub-tabs por locale activo). */
  locale: SupportedLocale
  /** Locales activos del documento (`Object.keys(definition.i18n)`).
   * Solo se renderean sub-tabs para estos. Si el documento solo tiene
   * `["es"]` activo, no se ve la tab EN. */
  activeLocales: string[]
  /** Modo expandido controlado por el padre. */
  expanded: boolean
  readOnly?: boolean
  onChange(next: TableColumn): void
  onRemove(): void
  onToggleExpand(): void
  /** Callback opcional para que el padre intercepte cambio de tipo
   * (p.ej. confirmar warning si pierde enum_values). Si retorna false,
   * el cambio se cancela. Default: aplica directo. */
  onBeforeTypeChange?: (
    from: TableColumn['type'],
    to: TableColumn['type'],
    hasEnumValues: boolean,
  ) => boolean
  /** ID estable para tests. */
  testId?: string
}

export function TableColumnEditor({
  column,
  keyDuplicate = false,
  locale,
  activeLocales,
  expanded,
  readOnly = false,
  onChange,
  onRemove,
  onToggleExpand,
  onBeforeTypeChange,
  testId,
}: TableColumnEditorProps) {
  const { t } = useTranslation('templates')
  // Sub-tab inicial: el locale activo del designer si está en la lista,
  // else el primer locale activo (fallback defensivo si el documento
  // tiene solo 'en' pero el designer renderea con locale='es').
  const initialTab =
    activeLocales.includes(locale) ? locale : (activeLocales[0] ?? 'es')
  const [labelTab, setLabelTab] = useState<string>(initialTab)

  const keyInvalid = column.key.length > 0 && !KEY_PATTERN.test(column.key)
  const labelInActive = column.i18n[locale]?.label ?? ''

  function updateKey(raw: string) {
    onChange({ ...column, key: raw })
  }
  function updateType(nextType: TableColumn['type']) {
    if (nextType === column.type) return
    const hasEnumValues = (column.enum_values ?? []).length > 0
    if (onBeforeTypeChange) {
      const allowed = onBeforeTypeChange(column.type, nextType, hasEnumValues)
      if (!allowed) return
    }
    // Si cambia a enum sin valores → inicializar array vacío para que el
    // EnumValuesEditor se monte y muestre warning de empty.
    // Si cambia FUERA de enum → limpiar enum_values (data, no solo UI).
    const next: TableColumn = { ...column, type: nextType }
    if (nextType === 'enum') {
      next.enum_values = column.enum_values ?? []
    } else {
      delete next.enum_values
    }
    // Doc 19 §10 — same dance para currency: cambia a currency → default
    // MXN para el publish-gate; cambia FUERA → limpiar ambos campos para
    // evitar data leak (el backend rechaza si quedan en otro type).
    if (nextType === 'currency') {
      if (!column.default_currency) next.default_currency = 'MXN'
    } else {
      delete next.default_currency
      delete next.allowed_currencies
    }
    // Doc 06 §Backlog — autonum: cambia a autonum → defaults razonables
    // ('ID-' + pad=3) + limpiar campos incompatibles (required/validation).
    // Cambia FUERA → limpiar prefix/pad (data leak guard del backend).
    if (nextType === 'autonum') {
      if (column.prefix === undefined || column.prefix === null) {
        next.prefix = 'ID-'
      }
      if (column.pad === undefined || column.pad === null) {
        next.pad = 3
      }
      // autonum NO acepta required ni validation — Pydantic rechaza.
      next.required = false
      delete next.validation
    } else {
      delete next.prefix
      delete next.pad
    }
    onChange(next)
  }

  function updatePrefix(value: string) {
    onChange({ ...column, prefix: value })
  }

  function updatePad(value: number) {
    onChange({ ...column, pad: value })
  }

  function updateDefaultCurrency(value: string) {
    const trimmed = value.trim().toUpperCase()
    const next: TableColumn = { ...column }
    if (trimmed) {
      next.default_currency = trimmed
    } else {
      delete next.default_currency
    }
    onChange(next)
  }

  function updateAllowedCurrencies(values: string[]) {
    const next: TableColumn = { ...column }
    const cleaned = values
      .map((v) => v.trim().toUpperCase())
      .filter((v) => /^[A-Z]{3}$/.test(v))
    // Dedupe preservando orden de aparición.
    const seen = new Set<string>()
    const deduped: string[] = []
    for (const v of cleaned) {
      if (!seen.has(v)) {
        seen.add(v)
        deduped.push(v)
      }
    }
    if (deduped.length > 0) {
      next.allowed_currencies = deduped
    } else {
      delete next.allowed_currencies
    }
    onChange(next)
  }
  function updateRequired(value: boolean) {
    onChange({ ...column, required: value })
  }
  function updateI18nField(
    loc: string,
    field: 'label' | 'placeholder',
    value: string,
  ) {
    const currentLoc = column.i18n[loc] ?? { label: '' }
    const nextLoc =
      field === 'label'
        ? { ...currentLoc, label: value }
        : { ...currentLoc, placeholder: value || null }
    const nextI18n = { ...column.i18n, [loc]: nextLoc }
    onChange({ ...column, i18n: nextI18n })
  }
  function updateValidation(rules: ValidationRule[]) {
    const next: TableColumn = { ...column }
    if (rules.length > 0) {
      next.validation = rules
    } else {
      delete next.validation
    }
    onChange(next)
  }
  function updateEnumValues(values: EnumValue[]) {
    onChange({ ...column, enum_values: values })
  }

  // Bloque de validación: solo aplica para plain_text (length rules).
  // Number/date/enum no tienen validaciones numéricas v2 catalog.
  const supportsValidation = column.type === 'plain_text'
  const validationBlockType: BlockType | null = supportsValidation
    ? 'plain_text'
    : null

  return (
    <li
      data-testid={testId}
      className={
        expanded
          ? 'rounded-md border border-brand/40 bg-canvas'
          : 'rounded-md border border-line bg-surface-muted/30'
      }
    >
      {/* Header colapsado / expandido */}
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onToggleExpand}
          aria-label={expanded ? 'collapse' : 'expand'}
          className="text-ink-muted hover:text-ink"
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
        <span
          className={
            keyInvalid || keyDuplicate
              ? 'font-mono text-xs text-critical'
              : 'font-mono text-xs text-ink-muted'
          }
        >
          {column.key || '(sin clave)'}
        </span>
        <span className="text-xs text-ink truncate flex-1">
          {labelInActive || (
            <span className="italic text-ink-subtle">
              {t('designer.editor.table_config_v2.column_no_label_fallback')}
            </span>
          )}
        </span>
        <span className="text-[10px] font-mono uppercase text-ink-subtle">
          {column.type}
        </span>
        {column.required ? (
          <span className="rounded bg-brand/10 px-1 text-[9px] uppercase tracking-wider text-brand">
            req
          </span>
        ) : null}
        {!readOnly ? (
          <button
            type="button"
            onClick={onRemove}
            aria-label={
              t('designer.editor.table_config_v2.remove_column') as string
            }
            className="text-ink-subtle hover:text-critical p-1"
            data-testid={testId ? `${testId}-remove` : undefined}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>

      {expanded ? (
        <div className="border-t border-line px-3 py-3 space-y-3">
          {/* Clave (key) */}
          <label className="block">
            <span className="text-xs text-ink-muted">
              {t('designer.editor.table_config_v2.column_key_label')}
            </span>
            <input
              type="text"
              value={column.key}
              onChange={(e) => updateKey(e.target.value)}
              disabled={readOnly}
              pattern="[a-z][a-z0-9_]*"
              data-testid={testId ? `${testId}-key` : undefined}
              className={
                keyInvalid || keyDuplicate
                  ? 'mt-1 w-full bg-canvas border border-critical/60 rounded-md px-2 py-1 text-sm font-mono focus:outline-none focus:border-critical disabled:opacity-60'
                  : 'mt-1 w-full bg-canvas border border-line rounded-md px-2 py-1 text-sm font-mono focus:outline-none focus:border-brand disabled:opacity-60'
              }
            />
            <span className="text-[11px] text-ink-subtle italic">
              {t('designer.editor.table_config_v2.column_key_helper')}
            </span>
            {keyDuplicate ? (
              <p
                className="text-[10px] text-critical mt-0.5"
                data-testid={testId ? `${testId}-duplicate-warning` : undefined}
              >
                {t(
                  'designer.editor.table_config_v2.duplicate_column_key_warning',
                )}
              </p>
            ) : null}
          </label>

          {/* Tipo + required en una fila */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-ink-muted">
                {t('designer.editor.table_config_v2.column_type_label')}
              </span>
              <select
                value={column.type}
                onChange={(e) => updateType(e.target.value as TableColumn['type'])}
                disabled={readOnly}
                data-testid={testId ? `${testId}-type` : undefined}
                className="mt-1 w-full bg-canvas border border-line rounded-md px-2 py-1 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
              >
                {COLUMN_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 mt-5">
              <input
                type="checkbox"
                checked={column.required === true}
                onChange={(e) => updateRequired(e.target.checked)}
                disabled={readOnly}
                data-testid={testId ? `${testId}-required` : undefined}
                className="rounded border-line"
              />
              <span className="text-sm text-ink">
                {t('designer.editor.table_config_v2.column_required_label')}
              </span>
            </label>
          </div>

          {/* Sub-tabs por locale activo — si solo hay 1 locale activo,
              omitir la barra de tabs (single-locale UX). */}
          <div>
            {activeLocales.length > 1 ? (
              <div
                className="flex gap-1 border-b border-line"
                data-testid={testId ? `${testId}-locale-tabs` : undefined}
              >
                {activeLocales.map((loc) => (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => setLabelTab(loc)}
                    className={
                      labelTab === loc
                        ? 'px-2 py-1 text-xs font-medium border-b-2 border-brand text-ink -mb-px'
                        : 'px-2 py-1 text-xs text-ink-muted hover:text-ink'
                    }
                    data-testid={
                      testId ? `${testId}-locale-tab-${loc}` : undefined
                    }
                  >
                    {loc.toUpperCase()}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="mt-2 space-y-2">
              <label className="block">
                <span className="text-xs text-ink-muted">
                  {t(
                    `designer.editor.table_config_v2.column_label_${labelTab}`,
                    { defaultValue: `Etiqueta (${labelTab})` },
                  )}
                </span>
                <input
                  type="text"
                  value={column.i18n[labelTab]?.label ?? ''}
                  onChange={(e) =>
                    updateI18nField(labelTab, 'label', e.target.value)
                  }
                  disabled={readOnly}
                  data-testid={
                    testId ? `${testId}-label-${labelTab}` : undefined
                  }
                  className="mt-1 w-full bg-canvas border border-line rounded-md px-2 py-1 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
                />
              </label>
              <label className="block">
                <span className="text-xs text-ink-muted">
                  {t(
                    `designer.editor.table_config_v2.column_placeholder_${labelTab}`,
                    { defaultValue: `Placeholder (${labelTab})` },
                  )}
                </span>
                <input
                  type="text"
                  value={column.i18n[labelTab]?.placeholder ?? ''}
                  onChange={(e) =>
                    updateI18nField(labelTab, 'placeholder', e.target.value)
                  }
                  disabled={readOnly}
                  data-testid={
                    testId ? `${testId}-placeholder-${labelTab}` : undefined
                  }
                  className="mt-1 w-full bg-canvas border border-line rounded-md px-2 py-1 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
                />
              </label>
            </div>
          </div>

          {/* Validation sub-block (solo plain_text) */}
          {validationBlockType ? (
            <ValidationRulesBlock
              blockType={validationBlockType}
              validations={column.validation ?? []}
              readOnly={readOnly}
              onChange={updateValidation}
            />
          ) : null}

          {/* Enum values (solo type=enum) */}
          {column.type === 'enum' ? (
            <EnumValuesEditor
              values={column.enum_values ?? []}
              activeLocales={activeLocales}
              readOnly={readOnly}
              onChange={updateEnumValues}
              testId={testId ? `${testId}-enum-values` : undefined}
            />
          ) : null}

          {/* Currency config (solo type=currency, Doc 19 §10) */}
          {column.type === 'currency' ? (
            <CurrencyConfigBlock
              defaultCurrency={column.default_currency ?? ''}
              allowedCurrencies={column.allowed_currencies ?? []}
              readOnly={readOnly}
              onChangeDefault={updateDefaultCurrency}
              onChangeAllowed={updateAllowedCurrencies}
              {...(testId ? { testId: `${testId}-currency` } : {})}
            />
          ) : null}

          {/* Autonum config (solo type=autonum, Doc 06 §Backlog) */}
          {column.type === 'autonum' ? (
            <AutonumConfigBlock
              prefix={column.prefix ?? ''}
              pad={column.pad ?? 0}
              readOnly={readOnly}
              onChangePrefix={updatePrefix}
              onChangePad={updatePad}
              {...(testId ? { testId: `${testId}-autonum` } : {})}
            />
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

/** Doc 19 §10 — bloque del Designer para configurar una columna
 * `type='currency'`: declarar `default_currency` (ISO 4217 obligatorio
 * para publish) y opcionalmente `allowed_currencies` (lista para
 * restringir el dropdown del editor).
 */
function CurrencyConfigBlock({
  defaultCurrency,
  allowedCurrencies,
  readOnly,
  onChangeDefault,
  onChangeAllowed,
  testId,
}: {
  defaultCurrency: string
  allowedCurrencies: string[]
  readOnly: boolean
  onChangeDefault: (value: string) => void
  onChangeAllowed: (values: string[]) => void
  testId?: string
}) {
  const { t } = useTranslation('templates')
  const allowedRaw = allowedCurrencies.join(', ')
  return (
    <div
      data-testid={testId}
      className="rounded-md border border-line bg-surface-muted/30 p-3 space-y-3"
    >
      <div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-muted">
            {t('designer.editor.table_config_v2.currency_default_label')}
          </span>
          <input
            type="text"
            value={defaultCurrency}
            onChange={(e) => onChangeDefault(e.target.value)}
            disabled={readOnly}
            placeholder="MXN"
            maxLength={3}
            className="mt-1 w-32 bg-canvas border border-line rounded-md px-2 py-1 text-sm font-mono uppercase tracking-wider focus:outline-none focus:border-brand disabled:opacity-50"
            aria-label={t('designer.editor.table_config_v2.currency_default_aria')}
          />
        </label>
        <p className="mt-1 text-[11px] text-ink-subtle italic">
          {t('designer.editor.table_config_v2.currency_default_help')}
        </p>
      </div>
      <div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-muted">
            {t('designer.editor.table_config_v2.currency_allowed_label')}
          </span>
          <input
            type="text"
            value={allowedRaw}
            onChange={(e) => {
              const parts = e.target.value
                .split(/[,\s]+/)
                .filter(Boolean)
              onChangeAllowed(parts)
            }}
            disabled={readOnly}
            placeholder="MXN, USD, EUR"
            className="mt-1 w-full bg-canvas border border-line rounded-md px-2 py-1 text-sm font-mono uppercase tracking-wider focus:outline-none focus:border-brand disabled:opacity-50"
            aria-label={t('designer.editor.table_config_v2.currency_allowed_aria')}
          />
        </label>
        <p className="mt-1 text-[11px] text-ink-subtle italic">
          {t('designer.editor.table_config_v2.currency_allowed_help')}
        </p>
      </div>
    </div>
  )
}

/** Doc 06 §Backlog — bloque del Designer para configurar una columna
 * `type='autonum'`: declarar `prefix` (puede ser '') y `pad` (0-10).
 * El editor del cell renderea el ID calculado a partir de `rowIndex + 1`,
 * `prefix` y `pad`. Política de borrado: gap permitido (igual que Jira /
 * Linear / GitHub Issues) — los IDs pueden estar referenciados externamente.
 */
function AutonumConfigBlock({
  prefix,
  pad,
  readOnly,
  onChangePrefix,
  onChangePad,
  testId,
}: {
  prefix: string
  pad: number
  readOnly: boolean
  onChangePrefix: (value: string) => void
  onChangePad: (value: number) => void
  testId?: string
}) {
  const { t } = useTranslation('templates')
  // Preview del primer ID generado — feedback inmediato del impacto de
  // prefix + pad. Si pad=3 + prefix='RSK-', el preview muestra 'RSK-001'.
  const preview =
    pad > 0 ? `${prefix}${String(1).padStart(pad, '0')}` : `${prefix}1`
  return (
    <div
      data-testid={testId}
      className="rounded-md border border-line bg-surface-muted/30 p-3 space-y-3"
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-muted">
              {t('designer.editor.table_config_v2.autonum_prefix_label')}
            </span>
            <input
              type="text"
              value={prefix}
              onChange={(e) => onChangePrefix(e.target.value)}
              disabled={readOnly}
              placeholder="RSK-"
              maxLength={20}
              pattern="[A-Za-z0-9_\-]*"
              className="mt-1 w-full bg-canvas border border-line rounded-md px-2 py-1 text-sm font-mono focus:outline-none focus:border-brand disabled:opacity-50"
              aria-label={t('designer.editor.table_config_v2.autonum_prefix_aria')}
            />
          </label>
          <p className="mt-1 text-[11px] text-ink-subtle italic">
            {t('designer.editor.table_config_v2.autonum_prefix_help')}
          </p>
        </div>
        <div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-muted">
              {t('designer.editor.table_config_v2.autonum_pad_label')}
            </span>
            <input
              type="number"
              value={pad}
              onChange={(e) => {
                const n = Number(e.target.value)
                if (Number.isFinite(n) && n >= 0 && n <= 10) {
                  onChangePad(n)
                }
              }}
              disabled={readOnly}
              min={0}
              max={10}
              className="mt-1 w-full bg-canvas border border-line rounded-md px-2 py-1 text-sm font-mono focus:outline-none focus:border-brand disabled:opacity-50"
              aria-label={t('designer.editor.table_config_v2.autonum_pad_aria')}
            />
          </label>
          <p className="mt-1 text-[11px] text-ink-subtle italic">
            {t('designer.editor.table_config_v2.autonum_pad_help')}
          </p>
        </div>
      </div>
      <div className="rounded border border-line bg-canvas/50 p-2">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-muted mb-1">
          {t('designer.editor.table_config_v2.autonum_preview_label')}
        </p>
        <p className="font-mono text-sm text-ink">{preview}</p>
      </div>
    </div>
  )
}
